import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, Upload, Check, Loader2, Plus, X, Globe, Search,
  Rocket, CreditCard, Package, Palette,
  ShoppingBag, Briefcase, PlayCircle, CalendarClock, FileText, Receipt,
  MapPin, Pencil, Star, Crown, HandCoins, GraduationCap, Sparkles, Wand2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ENLogo } from '@/components/ENLogo';
import { PALETTE_GROUPS } from './sqs/palettes';
import { imageForTopic } from './sqs/topicImages';
import { renderPremiumSection } from './premium/PremiumSectionRenderer';
import { SafeImg } from './SafeImg';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Step strip across the bottom — Squarespace-style
const STEP_STRIP = [
  { id: 'topic',     label: 'Topic' },
  { id: 'goals',     label: 'Goals' },
  { id: 'tone',      label: 'Tone' },
  { id: 'start',     label: 'Start' },
  { id: 'template',  label: 'Template' },
  { id: 'palette',   label: 'Palette' },
  { id: 'brand',     label: 'Brand' },
  { id: 'domain',    label: 'Domain' },
  { id: 'payments',  label: 'Payments' },
  { id: 'fonts',     label: 'Fonts' },
  { id: 'launch',    label: 'Launch' },
];

// 15 fonts grouped by tone — each option carries a Google Font name + a sensible body pairing
const FONT_OPTIONS = [
  // Professional (3)
  { id: 'inter',         name: 'Inter',              body: 'Inter',         family: 'Inter, sans-serif',                tone: 'Professional', recommended: true },
  { id: 'manrope',       name: 'Manrope',            body: 'Manrope',       family: 'Manrope, sans-serif',              tone: 'Professional' },
  { id: 'outfit',        name: 'Outfit',             body: 'Outfit',        family: 'Outfit, sans-serif',               tone: 'Professional' },
  // Playful (3)
  { id: 'caveat',        name: 'Caveat',             body: 'Inter',         family: '"Caveat", cursive',                tone: 'Playful' },
  { id: 'lobster',       name: 'Lobster',            body: 'Outfit',        family: '"Lobster", cursive',               tone: 'Playful' },
  { id: 'pacifico',      name: 'Pacifico',           body: 'Outfit',        family: '"Pacifico", cursive',              tone: 'Playful' },
  // Sophisticated (3)
  { id: 'playfair',      name: 'Playfair Display',   body: 'Lora',          family: '"Playfair Display", serif',        tone: 'Sophisticated' },
  { id: 'cormorant',     name: 'Cormorant Garamond', body: 'Outfit',        family: '"Cormorant Garamond", serif',      tone: 'Sophisticated' },
  { id: 'lora',          name: 'Lora',               body: 'Lora',          family: '"Lora", serif',                    tone: 'Sophisticated' },
  // Bold (3)
  { id: 'bebas',         name: 'Bebas Neue',         body: 'Manrope',       family: '"Bebas Neue", sans-serif',         tone: 'Bold' },
  { id: 'archivo-black', name: 'Archivo Black',      body: 'Outfit',        family: '"Archivo Black", sans-serif',      tone: 'Bold' },
  { id: 'anton',         name: 'Anton',              body: 'Inter',         family: '"Anton", sans-serif',              tone: 'Bold' },
  // Quirky (3)
  { id: 'fraunces',      name: 'Fraunces',           body: 'Manrope',       family: '"Fraunces", serif',                tone: 'Quirky' },
  { id: 'space-mono',    name: 'Space Mono',         body: 'Outfit',        family: '"Space Mono", monospace',          tone: 'Quirky' },
  { id: 'abril',         name: 'Abril Fatface',      body: 'Lora',          family: '"Abril Fatface", serif',           tone: 'Quirky' },
];

// One-time Google Fonts loader for the wizard preview
function loadFontStylesheet() {
  const id = 'wb-wizard-fonts';
  if (document.getElementById(id)) return;
  const families = [
    'Inter:wght@400;700', 'Manrope:wght@400;800', 'Outfit:wght@400;800',
    'Caveat:wght@700', 'Lobster', 'Pacifico',
    'Playfair+Display:wght@400;800', 'Cormorant+Garamond:wght@400;700', 'Lora:wght@400;700',
    'Bebas+Neue', 'Archivo+Black', 'Anton',
    'Fraunces:opsz,wght@9..144,800', 'Space+Mono:wght@700', 'Abril+Fatface',
    'Syne:wght@600;800',
  ];
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${families.map(f => 'family=' + f).join('&')}&display=swap`;
  document.head.appendChild(link);
}

// Indigenous-tailored popular topics (30+) — matches the marketplace categories
const POPULAR_TOPICS = [
  'Photography', 'Videography', 'Beadwork & Craft', 'Fashion & Apparel',
  'Music', 'Film & Video', 'Public Speaking', 'Coaching', 'Construction',
  'Wellness & Healing', 'Non-Profit Organization', 'Restaurant & Food',
  'Art & Painting', 'Workshops & Camps', 'Event Organizing',
  'Design Studio', 'Consulting', 'Real Estate', 'Tattoo Artist',
  'Salon & Beauty', 'Bakery & Coffee Shop', 'Wedding Services',
  'Online Courses & Education', 'Tour Guide & Outdoors',
  'Music Producer & DJ', 'Writer & Blogger', 'Yoga & Meditation',
  'Florist', 'Cleaning Services', 'Auto Repair', 'Pet Services',
  'Therapist & Counselor', 'Lawyer & Legal Services',
  'Personal Trainer & Fitness',
];

// 12 website goals
const GOAL_OPTIONS = [
  { id: 'sell_products',      label: 'Sell products',           icon: ShoppingBag },
  { id: 'sell_services',      label: 'Sell services',           icon: Briefcase },
  { id: 'sell_on_demand',     label: 'Sell on-demand videos',   icon: PlayCircle },
  { id: 'get_appointments',   label: 'Get appointments',        icon: CalendarClock },
  { id: 'contact_form',       label: 'Offer a contact form',    icon: FileText },
  { id: 'send_invoices',      label: 'Send invoices',           icon: Receipt },
  { id: 'promote_physical',   label: 'Promote a physical business', icon: MapPin },
  { id: 'publish_blog',       label: 'Publish a blog or other media', icon: Pencil },
  { id: 'showcase_work',      label: 'Showcase work / expertise', icon: Star },
  { id: 'sell_memberships',   label: 'Sell memberships',        icon: Crown },
  { id: 'collect_donations',  label: 'Collect donations',       icon: HandCoins },
  { id: 'sell_courses',       label: 'Sell online courses',     icon: GraduationCap },
];

// 6 brand tones
const TONE_OPTIONS = [
  { id: 'playful',       title: 'Playful',       desc: 'Fun. Light-hearted. Engaging.' },
  { id: 'friendly',      title: 'Friendly',      desc: 'Warm. Approachable. Helpful.' },
  { id: 'sophisticated', title: 'Sophisticated', desc: 'Refined. Cultured. Elegant.' },
  { id: 'bold',          title: 'Bold',          desc: 'Confident. Direct. Fearless.' },
  { id: 'quirky',        title: 'Quirky',        desc: 'Unconventional. Witty. Memorable.' },
  { id: 'innovative',    title: 'Innovative',    desc: 'Forward-thinking. Visionary. Pioneering.' },
];

// A single calm hero image per step — easy on the eyes
const STEP_IMAGES = {
  topic:    'https://images.unsplash.com/photo-1502136969935-8d8eef54d77b?w=1200&q=80',
  goals:    'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=1200&q=80',
  tone:     'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1200&q=80',
  start:    'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1200&q=80',
  template: 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=1200&q=80',
  palette:  'https://images.unsplash.com/photo-1558244402-286dd748c593?w=1200&q=80',
  brand:    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80',
  domain:   'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&q=80',
  products: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1200&q=80',
  payments: 'https://images.unsplash.com/photo-1556742400-b5b7c5121f9c?w=1200&q=80',
  fonts:    'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1200&q=80',
  launch:   'https://images.unsplash.com/photo-1465101046530-73398c7f28ca?w=1200&q=80',
};

export default function OnboardingWizard({ onComplete }) {
  // ── State for the new pre-template steps ──
  const [topic, setTopic] = useState('');
  const [topicSearch, setTopicSearch] = useState('');
  const [goals, setGoals] = useState([]);
  const [tone, setTone] = useState('');

  // ── Start choice: 'template' (pre-built) or 'scratch' (blank canvas)
  const [startMode, setStartMode] = useState('');

  // ── State for the existing steps ──
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [previewTpl, setPreviewTpl] = useState(null);
  const [selectedPalette, setSelectedPalette] = useState('fuchsia-violet');
  const [brandForm, setBrandForm] = useState({ brand_name: '', tagline: '', accent_color: '#1A4D2E' });
  const [domainType, setDomainType] = useState('subdomain');
  const [subdomain, setSubdomain] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [products, setProducts] = useState([{ name: '', price: '', category: '' }]);
  const [paymentSetup, setPaymentSetup] = useState({ stripe: false, etransfer: false });
  const [font, setFont] = useState('inter');
  const [loading, setLoading] = useState(false);

  // Effective step list — drops Template step if "from scratch"
  const effectiveSteps = useMemo(() => {
    let steps = STEP_STRIP;
    if (startMode === 'scratch') steps = steps.filter(s => s.id !== 'template');
    return steps;
  }, [startMode]);

  const [stepIdx, setStepIdx] = useState(0);
  const stepId = effectiveSteps[Math.min(stepIdx, effectiveSteps.length - 1)].id;

  // Load Google Fonts once
  useEffect(() => { loadFontStylesheet(); }, []);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const init = async () => {
      try {
        const siteRes = await axios.get(`${API}/builder/my-site`, { headers }).catch(() => null);
        if (!siteRes || siteRes.status === 404) {
          await axios.post(`${API}/builder/create-site`, { name: 'My Website' }, { headers });
        }
        const tplRes = await axios.get(`${API}/builder/templates`, { headers });
        setTemplates(tplRes.data?.builtin || []);
        const settingsRes = await axios.get(`${API}/builder/settings`, { headers });
        if (settingsRes.data?.brand_name) {
          setBrandForm(f => ({ ...f, brand_name: settingsRes.data.brand_name, tagline: settingsRes.data.tagline || '' }));
        }
      } catch (e) { /* swallow */ }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist progress + setup meta every advance
  const saveProgress = async (idx, extra = {}) => {
    try { await axios.put(`${API}/builder/onboarding-progress`, { step: idx, ...extra }, { headers }); } catch {}
  };

  const next = async (overrides = {}) => {
    await saveProgress(stepIdx + 1, overrides);
    setStepIdx(i => Math.min(effectiveSteps.length - 1, i + 1));
  };
  const back = () => setStepIdx(i => Math.max(0, i - 1));

  // ── Per-step actions ──
  const handleTopicNext = () => {
    const chosen = (topic || topicSearch).trim();
    if (!chosen) return toast.error('Pick or type a topic so we can tailor the site.');
    setTopic(chosen);
    next({ topic: chosen });
  };

  const handleGoalsNext = () => {
    if (goals.length === 0) return toast.error('Pick at least one goal.');
    next({ goals });
  };

  const handleToneNext = () => {
    if (!tone) return toast.error('Pick a brand tone.');
    next({ tone });
  };

  const applyTemplate = async () => {
    // From-scratch path: just save the choice and advance with a clean blank canvas
    if (startMode === 'scratch' || !selectedTemplate) {
      try {
        await axios.put(`${API}/builder/content`, { blocks: [] }, { headers });
      } catch {}
      return next();
    }
    setLoading(true);
    try {
      // Apply via the canonical backend endpoint (so block IDs, page, etc. are wired up)
      try {
        await axios.post(`${API}/builder/apply-template`, { template_id: selectedTemplate }, { headers });
      } catch {
        // Fallback to client-side blocks if endpoint not reachable
        const tpl = templates.find(t => t.id === selectedTemplate);
        if (tpl && tpl.blocks) {
          const blocks = (tpl.blocks || []).map(b => ({ ...b, id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random() }));
          await axios.put(`${API}/builder/content`, { blocks }, { headers });
        }
      }
      const tpl = templates.find(t => t.id === selectedTemplate);
      // Also persist the template's font choice so the builder + live site use it
      if (tpl && tpl.fonts) {
        try {
          await axios.put(`${API}/builder/settings`, {
            font_id: tpl.font_id || `${(tpl.fonts.heading || '').toLowerCase().replace(/\s+/g, '-')}`,
            font_family: tpl.fonts.heading,
            font_body: tpl.fonts.body,
          }, { headers });
        } catch { /* non-blocking */ }
      }
      toast.success(`"${tpl?.name || selectedTemplate}" template applied!`);
      await next({ template_id: selectedTemplate });
    } catch { toast.error('Failed to apply template'); }
    finally { setLoading(false); }
  };

  const saveBrand = async () => {
    if (!brandForm.brand_name) return toast.error('Brand name required');
    setLoading(true);
    try {
      await axios.put(`${API}/builder/settings`, brandForm, { headers });
      toast.success('Brand info saved!');
      await next();
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const saveProducts = async () => {
    setLoading(true);
    const valid = products.filter(p => p.name && p.price);
    try {
      for (const p of valid) {
        await axios.post(`${API}/builder/add-product`, {
          name: p.name, price: parseFloat(p.price), category: p.category || 'General',
          description: '', images: [], sizes: [], colors: [], is_active: true
        }, { headers });
      }
      if (valid.length) toast.success(`${valid.length} product(s) added!`);
      await next();
    } catch { toast.error('Failed to add products'); }
    finally { setLoading(false); }
  };

  const finishOnboarding = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/builder/finish-onboarding`, {}, { headers });
      toast.success("You're all set! Welcome to your dashboard.");
      onComplete?.();
    } catch { toast.error('Failed to complete'); }
    finally { setLoading(false); }
  };

  // ── Filtered topic suggestions ──
  const topicMatches = useMemo(() => {
    if (!topicSearch.trim()) return POPULAR_TOPICS;
    const q = topicSearch.toLowerCase();
    return POPULAR_TOPICS.filter(t => t.toLowerCase().includes(q));
  }, [topicSearch]);

  const saveFonts = async () => {
    setLoading(true);
    try {
      const f = FONT_OPTIONS.find(o => o.id === font);
      // Persist via settings (additive — backend stores unknown keys in the doc)
      await axios.put(`${API}/builder/settings`, { font_id: font, font_family: f?.family, font_body: f?.body }, { headers });
      toast.success(`"${f?.name}" font applied!`);
      await next({ font: font });
    } catch { toast.error('Failed to save font'); }
    finally { setLoading(false); }
  };

  const savePalette = async () => {
    setLoading(true);
    try {
      const all = PALETTE_GROUPS.flatMap(g => g.palettes);
      const p = all.find(x => x.id === selectedPalette);
      if (p) {
        await axios.put(`${API}/builder/settings`, {
          palette_id: p.id,
          palette_swatches: p.swatches,
          accent_color: p.swatches[3] || p.swatches[2],
        }, { headers });
        setBrandForm(f => ({ ...f, accent_color: p.swatches[3] || p.swatches[2] }));
        toast.success(`"${p.label}" palette applied!`);
      }
      await next({ palette_id: selectedPalette });
    } catch { toast.error('Failed to save palette'); }
    finally { setLoading(false); }
  };

  // ── NEXT button gating ──
  const canAdvance = () => {
    if (stepId === 'topic')    return !!(topic || topicSearch.trim());
    if (stepId === 'goals')    return goals.length > 0;
    if (stepId === 'tone')     return !!tone;
    if (stepId === 'start')    return !!startMode;
    if (stepId === 'template') return !!selectedTemplate;
    if (stepId === 'palette')  return !!selectedPalette;
    if (stepId === 'brand')    return !!brandForm.brand_name;
    if (stepId === 'domain')   return domainType === 'subdomain' ? !!subdomain : !!customDomain;
    if (stepId === 'fonts')    return !!font;
    return true;
  };

  // ── Primary action per step (also fires when NEXT is clicked) ──
  const handleNext = () => {
    if (!canAdvance()) {
      if (stepId === 'topic')    return toast.error('Pick or type a topic.');
      if (stepId === 'goals')    return toast.error('Pick at least one goal.');
      if (stepId === 'tone')     return toast.error('Pick a tone.');
      if (stepId === 'start')    return toast.error('Pick how you want to start.');
      if (stepId === 'template') return toast.error('Choose a template.');
      if (stepId === 'palette')  return toast.error('Pick a color palette.');
      if (stepId === 'brand')    return toast.error('Brand name required.');
      if (stepId === 'domain')   return toast.error('Enter a domain.');
      return;
    }
    if (stepId === 'topic')    return handleTopicNext();
    if (stepId === 'goals')    return handleGoalsNext();
    if (stepId === 'tone')     return handleToneNext();
    if (stepId === 'start')    return next({ start_mode: startMode });
    if (stepId === 'template') return applyTemplate();
    if (stepId === 'palette')  return savePalette();
    if (stepId === 'brand')    return saveBrand();
    if (stepId === 'domain')   return next();
    if (stepId === 'products') return saveProducts();
    if (stepId === 'payments') return next();
    if (stepId === 'fonts')    return saveFonts();
    if (stepId === 'launch')   return finishOnboarding();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-[#0E0E23] overflow-hidden" data-testid="onboarding-wizard">
      {/* Top bar */}
      <header className="absolute top-0 left-0 right-0 h-20 px-6 sm:px-10 flex items-center justify-between z-20">
        <ENLogo size="sm" linkTo="/" data-testid="onboarding-logo" />
        <button
          onClick={() => onComplete?.()}
          className="text-[10px] sm:text-xs tracking-[0.18em] font-bold text-white/60 hover:text-white uppercase"
          data-testid="onboarding-close-btn"
        >
          I&apos;m Just Browsing
        </button>
      </header>

      {/* Main 2-column grid */}
      <div className="h-full pt-20 pb-28 flex">
        {/* LEFT — content area */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-12 lg:px-20 py-8">
          <div className="max-w-2xl">
            {stepId === 'topic'    && <StepTopic value={topic} setValue={setTopic} search={topicSearch} setSearch={setTopicSearch} matches={topicMatches} />}
            {stepId === 'goals'    && <StepGoals selected={goals} setSelected={setGoals} />}
            {stepId === 'tone'     && <StepTone value={tone} setValue={setTone} />}
            {stepId === 'start'    && <StepStart value={startMode} setValue={setStartMode} />}
            {stepId === 'template' && <StepTemplate templates={templates} selected={selectedTemplate} setSelected={setSelectedTemplate} topic={topic} onPreview={setPreviewTpl} />}
            {stepId === 'palette'  && <StepPalette value={selectedPalette} setValue={setSelectedPalette} />}
            {stepId === 'brand'    && <StepBrand form={brandForm} setForm={setBrandForm} />}
            {stepId === 'domain'   && <StepDomain type={domainType} setType={setDomainType} sub={subdomain} setSub={setSubdomain} custom={customDomain} setCustom={setCustomDomain} />}
            {stepId === 'products' && null}
            {stepId === 'payments' && <StepPayments setup={paymentSetup} setSetup={setPaymentSetup} />}
            {stepId === 'fonts'    && <StepFonts value={font} setValue={setFont} />}
            {stepId === 'launch'   && <StepLaunch onLaunch={finishOnboarding} loading={loading} />}
          </div>
        </div>

        {/* RIGHT — decorative image panel (hidden on small screens) */}
        <aside className="hidden lg:block w-[28%] relative">
          <img
            src={topic && stepId !== 'launch' ? imageForTopic(topic) : (STEP_IMAGES[stepId] || imageForTopic(topic))}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`step-image-${stepId}`}
          />
          <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-transparent to-[#0E0E23]/60" />
        </aside>
      </div>

      {/* Bottom bar */}
      <footer className="absolute bottom-0 left-0 right-0 z-20">
        {/* Progress + step strip */}
        <div className="px-6 sm:px-12 lg:px-20 pb-3">
          <div className="max-w-3xl mx-auto">
            <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] transition-all duration-500"
                style={{ width: `${((stepIdx + 1) / effectiveSteps.length) * 100}%` }}
                data-testid="onboarding-progress-bar"
              />
            </div>
            <div className="flex items-center justify-between text-[10px] sm:text-xs">
              {effectiveSteps.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => { if (i < stepIdx) setStepIdx(i); }}
                  className={`whitespace-nowrap transition-all ${i === stepIdx ? 'text-white font-bold' : i < stepIdx ? 'text-white/60 hover:text-white' : 'text-white/30 cursor-default'}`}
                  data-testid={`step-label-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* BACK / NEXT */}
        <div className="px-6 sm:px-10 pb-6 flex items-center justify-between">
          <button
            onClick={back}
            disabled={stepIdx === 0}
            className="px-8 py-3 text-xs tracking-[0.18em] font-bold uppercase text-white hover:bg-[#15152E]/5 transition-colors border border-white/20 rounded-none disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="onboarding-back-btn"
          >
            <ArrowLeft size={14} className="inline mr-1 -mt-0.5" /> Back
          </button>

          {stepId !== 'launch' && (
            <button
              onClick={handleNext}
              disabled={loading || !canAdvance()}
              className="px-10 py-4 bg-[#1A1A1A] text-white text-xs tracking-[0.18em] font-bold uppercase hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="onboarding-next-btn"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <>Next <ArrowRight size={14} /></>}
            </button>
          )}
        </div>
      </footer>

      {previewTpl && (
        <TemplatePreviewModal
          template={previewTpl}
          onClose={() => setPreviewTpl(null)}
          onUse={(tpl) => { setSelectedTemplate(tpl.id); setPreviewTpl(null); toast.success('Selected — click Next to apply.'); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   STEPS
   ───────────────────────────────────────────────────────────── */

function StepHeader({ pre, title, subtitle }) {
  return (
    <div className="mb-10">
      {pre && <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/50 mb-3">{pre}</p>}
      <h1 className="text-[34px] sm:text-[44px] leading-[1.05] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, ui-sans-serif, system-ui' }}>{title}</h1>
      {subtitle && <p className="text-sm sm:text-base text-white/70 mt-4 max-w-md leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function StepTopic({ value, setValue, search, setSearch, matches }) {
  return (
    <div data-testid="step-topic">
      <StepHeader pre="Step 1 of 11" title="What's your site about?" subtitle="We'll tailor content and advice to your site's needs." />
      <div className="bg-[#15152E] border border-white/10 rounded-xl shadow-[0_2px_30px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <Search size={18} className="text-white/40" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setValue(''); }}
            placeholder="Search for your site topic"
            className="flex-1 bg-transparent outline-none text-[15px] text-white placeholder:text-white/40"
            data-testid="topic-search-input"
          />
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          <p className="px-5 pt-4 pb-2 text-[10px] tracking-[0.18em] font-bold uppercase text-white/45">Popular Topics</p>
          {matches.map((t) => (
            <button
              key={t}
              onClick={() => { setValue(t); setSearch(''); }}
              className={`w-full text-left px-5 py-3 text-[15px] text-white transition-colors border-t border-white/5 ${value === t ? 'bg-white/10 font-semibold' : 'hover:bg-white/[0.06]'}`}
              data-testid={`topic-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {t}
            </button>
          ))}
          {matches.length === 0 && (
            <p className="px-5 py-6 text-sm text-white/50">No matches \u2014 keep typing and we&apos;ll use what you enter.</p>
          )}
        </div>
      </div>
      {value && (
        <p className="mt-4 text-xs text-white/60" data-testid="topic-selected">
          Selected: <span className="font-bold text-white">{value}</span>
        </p>
      )}
    </div>
  );
}

function StepGoals({ selected, setSelected }) {
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  return (
    <div data-testid="step-goals">
      <StepHeader pre="Step 2 of 11" title={<>What do you want to do<br />with your website?</>} subtitle="Select all that apply \u2014 we'll wire the matching blocks and pages into your site." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        {GOAL_OPTIONS.map(({ id, label, icon: Icon }) => {
          const on = selected.includes(id);
          return (
            <button
              key={id}
              onClick={() => toggle(id)}
              className={`flex items-center gap-3 px-4 py-3.5 rounded-md border text-left text-[15px] transition-all ${on ? 'bg-[#A78BFA] text-[#0E0E23] border-[#A78BFA]' : 'bg-[#15152E] text-white border-white/15 hover:border-white/40'}`}
              data-testid={`goal-${id}`}
            >
              <span className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center shrink-0 ${on ? 'bg-[#15152E] border-white text-white' : 'border-white/30'}`}>
                {on && <Check size={12} strokeWidth={3} />}
              </span>
              <Icon size={16} className={on ? 'text-white/80' : 'text-white/60'} />
              <span className="font-medium">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepTone({ value, setValue }) {
  return (
    <div data-testid="step-tone">
      <StepHeader pre="Step 3 of 11" title="Pick the tone of your brand." subtitle="Your tone shapes the copy, color palette, and fonts suggested next." />
      <div className="space-y-3 max-w-xl">
        {TONE_OPTIONS.map((o) => {
          const on = value === o.id;
          return (
            <button
              key={o.id}
              onClick={() => setValue(o.id)}
              className={`w-full text-left px-6 py-5 rounded-md border transition-all ${on ? 'bg-[#A78BFA] text-[#0E0E23] border-[#A78BFA]' : 'bg-[#15152E] text-white border-white/15 hover:border-white/40'}`}
              data-testid={`tone-${o.id}`}
            >
              <p className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Syne, ui-sans-serif, system-ui' }}>{o.title}</p>
              <p className={`text-sm mt-1 ${on ? 'text-white/75' : 'text-white/60'}`}>{o.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepTemplate({ templates, selected, setSelected, topic, onPreview }) {
  // Score templates by topic relevance (keyword overlap with tags/category/name)
  // Premium templates get a tier boost so they float to the top when topic is empty
  // or only weakly matches.
  const ranked = useMemo(() => {
    if (!templates || templates.length === 0) return [];
    const q = (topic || '').toLowerCase();
    const score = (t) => {
      let s = 0;
      // Premium tier boost (visible-by-default; topic matches still win)
      if (t.tier === 'premium') s += 0.5;
      if (!q) return s;
      const hay = [
        t.name, t.description, t.category,
        ...(t.tags || []),
      ].join(' ').toLowerCase();
      q.split(/[^a-z]+/).filter(Boolean).forEach(token => {
        if (token.length < 3) return;
        if (hay.includes(token)) s += 1;
      });
      return s;
    };
    return [...templates].sort((a, b) => score(b) - score(a));
  }, [templates, topic]);

  return (
    <div data-testid="step-template">
      <StepHeader pre="Step 5 of 11" title="Choose a starting template." subtitle={`Hand-picked designs${topic ? ` for ${topic.toLowerCase()}` : ''} — preview any one before you commit.`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {ranked.map((tpl) => {
          const on = selected === tpl.id;
          const palette = tpl.palette || ['#15152E', '#A78BFA', '#8B5CF6', '#6D28D9', '#0E0E23'];
          const preview = tpl.preview || tpl.colors?.primary;
          return (
            <div
              key={tpl.id}
              className={`relative rounded-lg overflow-hidden border-2 text-left transition-all bg-[#15152E] ${on ? 'border-[#A78BFA] shadow-[0_8px_30px_rgba(139,92,246,0.35)]' : 'border-white/10 hover:border-white/30'}`}
              data-testid={`onboarding-template-${tpl.id}`}
            >
              <button onClick={() => setSelected(tpl.id)} className="block w-full text-left">
                <div className="relative h-32 bg-[#0E0E23] overflow-hidden">
                  {preview && typeof preview === 'string' && preview.startsWith('http') ? (
                    <SafeImg src={preview} alt={tpl.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${palette[2] || '#A78BFA'}, ${palette[3] || '#6D28D9'})` }} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  {on && <div className="absolute top-2 right-2 w-7 h-7 bg-[#A78BFA] rounded-full flex items-center justify-center"><Check size={14} className="text-white" strokeWidth={3} /></div>}
                  <div className="absolute bottom-2 left-2 flex gap-1">
                    {palette.slice(0, 5).map((c, i) => (
                      <span key={i} className="w-3.5 h-3.5 rounded-full border border-black/40" style={{ background: c }} />
                    ))}
                  </div>
                  {tpl.section_count && (
                    <span className="absolute top-2 left-2 text-[10px] tracking-[0.18em] font-bold uppercase text-white bg-black/40 px-2 py-1 rounded">
                      {tpl.section_count} sections
                    </span>
                  )}
                  {tpl.tier === 'premium' && (
                    <span className="absolute top-2 right-10 text-[10px] tracking-[0.18em] font-bold uppercase text-black bg-gradient-to-r from-amber-300 to-yellow-200 px-2 py-1 rounded" data-testid={`premium-badge-${tpl.id}`}>
                      ★ Premium
                    </span>
                  )}
                </div>
              </button>
              <div className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0" onClick={() => setSelected(tpl.id)}>
                  <p className="text-[15px] font-bold text-white">{tpl.name}</p>
                  <p className="text-xs text-white/60 mt-0.5 line-clamp-2">{tpl.description}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onPreview(tpl); }}
                  className="shrink-0 text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA] hover:text-white px-2.5 py-1.5 border border-[#A78BFA]/40 hover:border-[#A78BFA] rounded transition-all"
                  data-testid={`preview-template-${tpl.id}`}
                >
                  Preview
                </button>
              </div>
            </div>
          );
        })}
        {ranked.length === 0 && (
          <p className="col-span-2 text-sm text-white/55 py-6">Loading templates…</p>
        )}
      </div>
    </div>
  );
}

// Full-page template preview modal — renders all sections in a scrollable mock-browser
function TemplatePreviewModal({ template, onClose, onUse }) {
  const [device, setDevice] = useState('desktop'); // 'desktop' | 'phone'
  if (!template) return null;
  const palette = template.palette || ['#FAFAFA', '#E5DDFF', '#A78BFA', '#8B5CF6', '#0E0E23'];
  const bg = palette[0]; const text = palette[4];
  const isPhone = device === 'phone';
  return (
    <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-md flex items-stretch" data-testid="template-preview-modal">
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#0E0E23] border-b border-white/10">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/80 hover:text-white border border-white/20 hover:border-white/40 rounded"
              data-testid="preview-back-btn"
              aria-label="Back to template list"
            >
              <ArrowLeft size={14} />
              Back
            </button>
            <div>
              <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/60">Template Preview</p>
              <h2 className="text-xl font-bold text-white mt-0.5" style={{ fontFamily: 'Syne, ui-sans-serif, system-ui' }}>{template.name}</h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Device toggle */}
            <div className="flex bg-white/5 rounded p-1 border border-white/10" data-testid="device-toggle">
              <button
                onClick={() => setDevice('desktop')}
                className={`px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase font-bold rounded transition-all ${device === 'desktop' ? 'bg-white text-[#0E0E23]' : 'text-white/60 hover:text-white'}`}
                data-testid="preview-device-desktop"
              >
                Desktop
              </button>
              <button
                onClick={() => setDevice('phone')}
                className={`px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase font-bold rounded transition-all ${device === 'phone' ? 'bg-white text-[#0E0E23]' : 'text-white/60 hover:text-white'}`}
                data-testid="preview-device-phone"
              >
                Phone
              </button>
            </div>
            <button onClick={onClose} className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/70 hover:text-white border border-white/20 rounded" data-testid="preview-close-btn">Close</button>
            <button onClick={() => onUse(template)} className="px-6 py-2 text-xs tracking-[0.18em] uppercase font-bold text-[#0E0E23] bg-[#A78BFA] hover:bg-white rounded" data-testid="preview-use-btn">Use this template</button>
          </div>
        </div>
        {/* Preview scroll area */}
        <div className="flex-1 overflow-y-auto flex justify-center" style={{ background: isPhone ? '#1a1a2e' : bg, color: text, padding: isPhone ? '32px 0' : '0' }}>
          {/* Phone-preview CSS overrides — force single-column + smaller text inside the 390px frame */}
          <style>{`
            .wb-phone-preview .grid { grid-template-columns: 1fr !important; gap: 16px !important; }
            .wb-phone-preview .md\\:grid-cols-2,
            .wb-phone-preview .md\\:grid-cols-3,
            .wb-phone-preview .md\\:grid-cols-4,
            .wb-phone-preview .md\\:grid-cols-5,
            .wb-phone-preview .md\\:grid-cols-6,
            .wb-phone-preview .md\\:grid-cols-12,
            .wb-phone-preview .sm\\:grid-cols-2,
            .wb-phone-preview .sm\\:grid-cols-3 { grid-template-columns: 1fr !important; }
            .wb-phone-preview .grid-cols-2,
            .wb-phone-preview .grid-cols-3,
            .wb-phone-preview .grid-cols-4 { grid-template-columns: 1fr !important; }
            .wb-phone-preview img { max-width: 100% !important; height: auto !important; max-height: 280px !important; object-fit: cover !important; }
            .wb-phone-preview section { padding-left: 18px !important; padding-right: 18px !important; padding-top: 36px !important; padding-bottom: 36px !important; }
            .wb-phone-preview h1, .wb-phone-preview .text-7xl, .wb-phone-preview .text-8xl { font-size: 2rem !important; line-height: 1.1 !important; }
            .wb-phone-preview h2, .wb-phone-preview .text-5xl, .wb-phone-preview .text-6xl { font-size: 1.5rem !important; line-height: 1.15 !important; }
            .wb-phone-preview .text-4xl { font-size: 1.4rem !important; }
            .wb-phone-preview .text-3xl { font-size: 1.25rem !important; }
            .wb-phone-preview .text-2xl { font-size: 1.1rem !important; }
            .wb-phone-preview .text-xl, .wb-phone-preview .text-lg { font-size: 1rem !important; }
            .wb-phone-preview .min-h-\\[640px\\], .wb-phone-preview .min-h-\\[560px\\], .wb-phone-preview .min-h-\\[480px\\], .wb-phone-preview .min-h-\\[440px\\], .wb-phone-preview .min-h-\\[420px\\] { min-height: 380px !important; }
            .wb-phone-preview .max-w-7xl, .wb-phone-preview .max-w-6xl, .wb-phone-preview .max-w-5xl, .wb-phone-preview .max-w-4xl, .wb-phone-preview .max-w-3xl, .wb-phone-preview .max-w-2xl { max-width: 100% !important; }
            .wb-phone-preview .columns-3, .wb-phone-preview .columns-2 { columns: 1 !important; }
            .wb-phone-preview .h-\\[600px\\], .wb-phone-preview .h-\\[480px\\] { height: 260px !important; }
            .wb-phone-preview .px-16, .wb-phone-preview .px-8, .wb-phone-preview .md\\:px-16 { padding-left: 18px !important; padding-right: 18px !important; }
            .wb-phone-preview .py-32, .wb-phone-preview .py-24, .wb-phone-preview .py-20, .wb-phone-preview .py-16 { padding-top: 40px !important; padding-bottom: 40px !important; }
            .wb-phone-preview .gap-12, .wb-phone-preview .gap-10, .wb-phone-preview .gap-8 { gap: 20px !important; }
          `}</style>
          {isPhone ? (
            <div className="relative" style={{ width: 390, maxWidth: '100%' }} data-testid="preview-phone-frame">
              <div className="wb-phone-preview rounded-[40px] overflow-hidden shadow-2xl border-[10px] border-black" style={{ background: bg, color: text, maxHeight: '85vh', overflowY: 'auto' }}>
                <TemplatePreviewHeader template={template} palette={palette} />
                {(template.blocks || []).map((b, i) => (
                  <TemplatePreviewBlock key={i} block={b} palette={palette} template={template} />
                ))}
                <TemplatePreviewFooter template={template} palette={palette} />
              </div>
              <p className="text-center text-xs text-white/40 mt-4 tracking-widest">PHONE PREVIEW · 390 × auto</p>
            </div>
          ) : (
            <div className="w-full max-w-[1280px] mx-auto" data-testid="preview-desktop-frame">
              <TemplatePreviewHeader template={template} palette={palette} />
              {(template.blocks || []).map((b, i) => (
                <TemplatePreviewBlock key={i} block={b} palette={palette} template={template} />
              ))}
              <TemplatePreviewFooter template={template} palette={palette} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplatePreviewHeader({ template, palette }) {
  // Show a tasteful brand header in every template preview so users see what
  // a finished site will look like — was missing before, leaving previews
  // looking unfinished.
  const [bg, , accent, , ink] = palette;
  const brand = (template?.name || 'Your Brand').toUpperCase();
  const tplHeading = (template && template.fonts && template.fonts.heading) || 'Syne';
  return (
    <header
      className="w-full px-6 md:px-10 py-5 flex items-center justify-between border-b"
      style={{ background: bg, color: ink, borderColor: ink + '15' }}
      data-testid="preview-header"
    >
      <p className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: `'${tplHeading}', ui-sans-serif, system-ui`, color: ink }}>
        {brand}
      </p>
      <nav className="hidden md:flex items-center gap-7 text-sm" style={{ color: ink }}>
        {['Shop', 'About', 'Services', 'Contact'].map(l => (
          <span key={l} className="opacity-80 hover:opacity-100 cursor-pointer">{l}</span>
        ))}
        <span className="px-4 py-2 text-xs font-bold tracking-wider uppercase rounded" style={{ background: accent, color: '#fff' }}>Book</span>
      </nav>
    </header>
  );
}

function TemplatePreviewFooter({ template, palette }) {
  // Footer that mirrors a real production site (3-col + newsletter strip).
  const [, , accent, deep] = palette;
  const brand = template?.name || 'Your Brand';
  const tplHeading = (template && template.fonts && template.fonts.heading) || 'Syne';
  return (
    <footer
      className="w-full px-6 md:px-10 py-12 mt-2"
      style={{ background: deep, color: '#fff' }}
      data-testid="preview-footer"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <p className="text-2xl font-bold" style={{ fontFamily: `'${tplHeading}', ui-sans-serif, system-ui` }}>{brand}</p>
          <p className="mt-3 text-xs opacity-70 tracking-[0.2em] uppercase">Instagram · TikTok · Facebook</p>
        </div>
        <div>
          <p className="text-xs tracking-[0.18em] uppercase opacity-70 mb-3">Explore</p>
          <div className="space-y-1.5 text-sm opacity-90">
            <p>Home</p><p>About</p><p>Shop</p><p>Contact</p>
          </div>
        </div>
        <div>
          <p className="text-xs tracking-[0.18em] uppercase opacity-70 mb-3">Newsletter</p>
          <div className="flex gap-2">
            <span className="flex-1 rounded px-3 py-2 text-xs bg-white/10 border border-white/15 opacity-60">your@email.com</span>
            <span className="px-3 py-2 text-xs font-bold tracking-wider uppercase rounded" style={{ background: accent }}>Join</span>
          </div>
        </div>
      </div>
      <p className="mt-8 text-xs opacity-50 border-t border-white/15 pt-4 max-w-6xl mx-auto">
        © {new Date().getFullYear()} {brand}. Built on EntrepreNatives.
      </p>
    </footer>
  );
}

function TemplatePreviewBlock({ block, palette, template }) {
  const c = block.content || {};
  const [bg, , accent, deep, ink] = palette;
  // Use the template's actual heading font when available; fall back to theme/Syne
  const tplHeading = (template && template.fonts && template.fonts.heading) || '';
  const themeId = (block.theme_id || '').toString();
  const headingFont = tplHeading || (
    themeId.includes('luxury') ? 'Playfair Display' :
    themeId.includes('fashion') ? 'Bebas Neue' :
    themeId.includes('events') ? 'Anton' :
    themeId.includes('portfolio') ? 'Fraunces' :
    themeId.includes('consulting') ? 'Manrope' :
    themeId.includes('marketplace') ? 'Outfit' : 'Syne'
  );
  const headFamily = `'${headingFont}', ui-sans-serif, system-ui, sans-serif`;
  const premium = renderPremiumSection(block, palette, headingFont, 8);
  if (premium) return premium;
  switch (block.type) {
    case 'hero':
      return (
        <section className="relative min-h-[480px] flex items-center justify-center text-center px-6 py-20" style={{ backgroundColor: deep, color: '#fff' }}>
          {c.image_url && <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />}
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold leading-tight" style={{ fontFamily: headFamily }}>{c.title}</h1>
            {c.tagline && <p className="mt-6 text-lg opacity-90">{c.tagline}</p>}
            {(c.buttons || []).length > 0 && (
              <div className="mt-8 flex justify-center gap-3">
                {c.buttons.map((b, i) => (
                  <span key={i} className="px-7 py-3 text-sm font-bold tracking-wider uppercase rounded" style={{ background: i === 0 ? accent : 'transparent', color: '#fff', border: i === 0 ? 'none' : '1px solid #fff' }}>{b.text}</span>
                ))}
              </div>
            )}
          </div>
        </section>
      );
    case 'marquee':
      return (
        <section className="py-6 overflow-hidden whitespace-nowrap text-center" style={{ background: c.bg_color || ink, color: c.text_color || '#fff' }}>
          <p className="text-2xl font-bold inline-block" style={{ fontFamily: headFamily, animation: 'wb-scroll 30s linear infinite' }}>
            {(c.words || []).join(' · ')} · {(c.words || []).join(' · ')}
          </p>
        </section>
      );
    case 'about':
      return (
        <section className={`px-6 py-16 max-w-5xl mx-auto ${c.image_url ? 'grid grid-cols-1 md:grid-cols-2 gap-10 items-center' : 'text-center'}`}>
          {c.image_url && <SafeImg src={c.image_url} alt="" className="w-full h-72 object-cover rounded" />}
          <div className={c.image_url ? '' : 'max-w-3xl mx-auto'}>
            <h2 className="text-3xl font-bold" style={{ fontFamily: headFamily }}>{c.title}</h2>
            <p className="mt-4 opacity-80 leading-relaxed">{c.text}</p>
            {c.vision && <p className="mt-3 italic opacity-70">"{c.vision}"</p>}
          </div>
        </section>
      );
    case 'gallery':
      return (
        <section className="px-6 py-16 max-w-6xl mx-auto">
          {c.title && <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: headFamily }}>{c.title}</h2>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {(c.images || []).slice(0, 6).map((img, i) => (
              <SafeImg key={i} src={img} alt="" className="w-full h-56 object-cover rounded" />
            ))}
          </div>
        </section>
      );
    case 'products': {
      // Pull sample images from the template's other blocks (gallery / hero) so
      // we never render empty gray boxes in the preview.
      const sampleImgs = [];
      (template?.blocks || []).forEach(bb => {
        const bc = bb.content || {};
        if (Array.isArray(bc.images)) sampleImgs.push(...bc.images);
        else if (bc.image_url) sampleImgs.push(bc.image_url);
      });
      const productNames = ['The Studio Edit', 'House Signature', 'Limited Release'];
      const productPrices = ['From $48', 'From $124', 'From $260'];
      return (
        <section className="px-6 py-16 max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold" style={{ fontFamily: headFamily }}>{c.title || 'Featured'}</h2>
          {c.subtitle && <p className="mt-3 opacity-70">{c.subtitle}</p>}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[0, 1, 2].map(i => {
              const img = sampleImgs[(i * 2) % Math.max(sampleImgs.length, 1)];
              return (
                <div key={i} className="rounded overflow-hidden text-left" style={{ border: `1px solid ${accent}40`, background: '#fff' }}>
                  {img ? <SafeImg src={img} alt="" className="w-full aspect-square object-cover" /> : <div className="aspect-square" style={{ background: accent + '20' }} />}
                  <div className="p-4">
                    <p className="font-bold" style={{ color: ink }}>{productNames[i]}</p>
                    <p className="text-sm opacity-70 mt-1" style={{ color: ink }}>{productPrices[i]}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      );
    }
    case 'testimonial':
      return (
        <section className="px-6 py-20 max-w-3xl mx-auto text-center" style={{ background: bg }}>
          <p className="text-2xl italic leading-relaxed" style={{ fontFamily: headFamily }}>"{c.quote}"</p>
          <p className="mt-6 text-sm font-bold tracking-wider uppercase opacity-80">— {c.author}{c.role ? `, ${c.role}` : ''}</p>
        </section>
      );
    case 'form':
      return (
        <section className="px-6 py-16 max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-2 text-center" style={{ fontFamily: headFamily }}>{c.title}</h2>
          {c.description && <p className="opacity-70 mb-6 text-center">{c.description}</p>}
          <div className="space-y-3">
            {(c.fields || [{ label: 'Name' }, { label: 'Email' }, { label: 'Message' }]).map((f, i) => (
              <div key={i} className="rounded px-4 py-3" style={{ border: `1px solid ${accent}40` }}>
                <span className="opacity-50 text-sm">{f.label}</span>
              </div>
            ))}
            <span className="inline-block px-7 py-3 text-sm font-bold tracking-wider uppercase rounded" style={{ background: accent, color: '#fff' }}>Send</span>
          </div>
        </section>
      );
    case 'signup':
      return (
        <section className="px-6 py-16 text-center" style={{ background: deep, color: '#fff' }}>
          <h3 className="text-2xl font-bold" style={{ fontFamily: headFamily }}>{c.title}</h3>
          {c.subtitle && <p className="mt-2 opacity-80">{c.subtitle}</p>}
          <div className="mt-6 max-w-md mx-auto flex gap-2">
            <span className="flex-1 rounded px-4 py-3 bg-white/10 text-left opacity-60">your@email.com</span>
            <span className="px-6 py-3 rounded font-bold uppercase tracking-wider text-sm" style={{ background: accent }}>Subscribe</span>
          </div>
        </section>
      );
    case 'banner':
      return (
        <section className="relative px-6 py-16 text-center text-white" style={{ backgroundColor: deep }}>
          {c.image_url && <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
          <div className="absolute inset-0 bg-black/40" />
          <p className="relative text-2xl font-bold" style={{ fontFamily: headFamily }}>{c.text}</p>
        </section>
      );
    case 'donations':
      return (
        <section className="px-6 py-16 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold" style={{ fontFamily: headFamily }}>{c.title}</h2>
          {c.subtitle && <p className="mt-3 opacity-70">{c.subtitle}</p>}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {(c.amounts || [25, 50, 100, 250]).map(a => (
              <span key={a} className="px-6 py-3 rounded font-bold" style={{ border: `1px solid ${accent}`, color: accent }}>${a}</span>
            ))}
          </div>
        </section>
      );
    case 'booking':
      return (
        <section className="px-6 py-16 max-w-4xl mx-auto text-center" style={{ background: bg }}>
          <h2 className="text-3xl font-bold" style={{ fontFamily: headFamily }}>{c.title}</h2>
          {c.subtitle && <p className="mt-3 opacity-70">{c.subtitle}</p>}
          <span className="inline-block mt-6 px-8 py-3 rounded font-bold uppercase tracking-wider text-sm" style={{ background: accent, color: '#fff' }}>Book now</span>
        </section>
      );
    case 'video':
      return (
        <section className="px-6 py-16 max-w-4xl mx-auto text-center">
          {c.title && <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: headFamily }}>{c.title}</h3>}
          <div className="aspect-video rounded" style={{ background: accent + '20', border: `1px solid ${accent}40` }} />
        </section>
      );
    case 'single_image':
      return c.image_url ? <SafeImg src={c.image_url} alt="" className="w-full max-h-[600px] object-cover" /> : null;
    case 'custom_text':
      return (
        <section className="px-6 py-16 max-w-3xl mx-auto" style={{ textAlign: c.alignment || 'center' }}>
          {c.title && <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: headFamily }}>{c.title}</h2>}
          {c.text && <p className="opacity-80 leading-relaxed">{c.text}</p>}
        </section>
      );
    default:
      return null;
  }
}

function StepStart({ value, setValue }) {
  const cards = [
    { id: 'template', title: 'Start from a template', desc: 'Pre-built sections, images, and copy — ready to customize.', icon: Sparkles },
    { id: 'scratch',  title: 'Build from scratch',    desc: 'A blank canvas — add sections one at a time.',           icon: Wand2 },
  ];
  return (
    <div data-testid="step-start">
      <StepHeader pre="Step 4 of 11" title="How do you want to start?" subtitle="You can switch tracks any time — both paths give you the full editor." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
        {cards.map(({ id, title, desc, icon: Icon }) => {
          const on = value === id;
          return (
            <button
              key={id}
              onClick={() => setValue(id)}
              className={`relative text-left px-6 py-6 rounded-lg border-2 transition-all ${on ? 'bg-[#A78BFA] text-[#0E0E23] border-[#A78BFA]' : 'bg-[#15152E] text-white border-white/15 hover:border-white/40'}`}
              data-testid={`start-${id}`}
            >
              <Icon size={28} className={`mb-3 ${on ? 'text-[#0E0E23]' : 'text-[#A78BFA]'}`} />
              <p className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Syne, ui-sans-serif, system-ui' }}>{title}</p>
              <p className={`text-sm mt-2 ${on ? 'text-[#0E0E23]/75' : 'text-white/60'}`}>{desc}</p>
              {on && <Check size={18} className="absolute top-3 right-3" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepPalette({ value, setValue }) {
  return (
    <div data-testid="step-palette">
      <StepHeader pre="Step 6 of 11" title="Pick a color palette." subtitle="These determine your site's backgrounds, accents, and text — fully editable later." />
      <div className="space-y-8 max-w-3xl">
        {PALETTE_GROUPS.map((group) => (
          <div key={group.group}>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/50 mb-3">{group.group}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {group.palettes.map((p) => {
                const on = value === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setValue(p.id)}
                    className={`relative text-left rounded-lg border-2 overflow-hidden transition-all ${on ? 'border-[#A78BFA] shadow-[0_4px_20px_rgba(139,92,246,0.3)]' : 'border-white/10 hover:border-white/30'}`}
                    data-testid={`palette-${p.id}`}
                  >
                    <div className="flex h-14">
                      {p.swatches.map((c, i) => (
                        <span key={i} className="flex-1" style={{ background: c }} />
                      ))}
                    </div>
                    <div className="px-4 py-2.5 bg-[#15152E] flex items-center justify-between">
                      <span className="text-[13px] font-bold text-white">{p.label}</span>
                      {on && <Check size={14} className="text-[#A78BFA]" strokeWidth={3} />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepBrand({ form, setForm }) {
  return (
    <div data-testid="step-brand">
      <StepHeader pre="Step 7 of 11" title="Set up your brand." subtitle="A name and one-line tagline. You can polish this later." />
      <div className="space-y-5 max-w-lg">
        <div>
          <Label className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/60">Brand Name</Label>
          <Input
            value={form.brand_name}
            onChange={(e) => setForm(f => ({ ...f, brand_name: e.target.value }))}
            className="bg-[#15152E] border-white/15 text-white mt-2 rounded-md h-12 text-base"
            placeholder="Your business name"
            data-testid="brand-name-input"
          />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/60">Tagline</Label>
          <Input
            value={form.tagline}
            onChange={(e) => setForm(f => ({ ...f, tagline: e.target.value }))}
            className="bg-[#15152E] border-white/15 text-white mt-2 rounded-md h-12 text-base"
            placeholder="What makes you unique?"
            data-testid="tagline-input"
          />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/60">Accent Color</Label>
          <div className="flex gap-3 mt-2 flex-wrap">
            {['#1A4D2E', '#8B5CF6', '#E63946', '#FF6B35', '#2D6A4F', '#3B82F6', '#F59E0B', '#EC4899', '#0A0A0A', '#8B4513'].map(c => (
              <button
                key={c}
                onClick={() => setForm(f => ({ ...f, accent_color: c }))}
                className={`w-10 h-10 rounded-full transition-all ${form.accent_color === c ? 'ring-2 ring-[#1A1A1A] ring-offset-2 ring-offset-[#FAF7F2] scale-110' : 'hover:scale-110'}`}
                style={{ background: c }}
                data-testid={`accent-${c.replace('#','')}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepDomain({ type, setType, sub, setSub, custom, setCustom }) {
  return (
    <div data-testid="step-domain">
      <StepHeader pre="Step 8 of 11" title="Set up your domain." subtitle="Pick a free subdomain to launch fast, or connect your own domain." />
      <div className="space-y-3 max-w-xl">
        <button
          onClick={() => setType('subdomain')}
          className={`w-full flex items-center gap-4 px-5 py-5 rounded-md border text-left transition-all ${type === 'subdomain' ? 'bg-[#A78BFA] text-[#0E0E23] border-[#A78BFA]' : 'bg-[#15152E] text-white border-white/15 hover:border-white/40'}`}
          data-testid="domain-subdomain"
        >
          <Globe size={20} />
          <div className="flex-1">
            <p className="text-[15px] font-bold">Free Subdomain</p>
            <p className={`text-xs mt-0.5 ${type === 'subdomain' ? 'text-white/70' : 'text-white/60'}`}>yourname.entreprenatives.com \u2014 included free</p>
          </div>
          {type === 'subdomain' && <Check size={18} />}
        </button>
        <button
          onClick={() => setType('custom')}
          className={`w-full flex items-center gap-4 px-5 py-5 rounded-md border text-left transition-all ${type === 'custom' ? 'bg-[#A78BFA] text-[#0E0E23] border-[#A78BFA]' : 'bg-[#15152E] text-white border-white/15 hover:border-white/40'}`}
          data-testid="domain-custom"
        >
          <Globe size={20} />
          <div className="flex-1">
            <p className="text-[15px] font-bold">Custom Domain</p>
            <p className={`text-xs mt-0.5 ${type === 'custom' ? 'text-white/70' : 'text-white/60'}`}>Connect a domain you already own \u2014 available on paid plans</p>
          </div>
          {type === 'custom' && <Check size={18} />}
        </button>

        {type === 'subdomain' && (
          <div className="pt-2">
            <Label className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/60">Choose your subdomain</Label>
            <div className="flex items-center gap-0 mt-2">
              <Input
                value={sub}
                onChange={(e) => setSub(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="yourname"
                className="bg-[#15152E] border-white/15 text-white rounded-l-md rounded-r-none h-12 text-base flex-1"
                data-testid="subdomain-input"
              />
              <div className="h-12 px-4 bg-[#15152E]/[0.06] border border-white/15 border-l-0 rounded-r-md flex items-center text-white/70 text-sm font-mono">.entreprenatives.com</div>
            </div>
          </div>
        )}

        {type === 'custom' && (
          <div className="pt-2">
            <Label className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/60">Your custom domain</Label>
            <Input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="yourbusiness.com"
              className="bg-[#15152E] border-white/15 text-white rounded-md h-12 text-base mt-2"
              data-testid="custom-domain-input"
            />
            <p className="text-[11px] text-white/55 mt-1.5">You&apos;ll configure DNS settings after onboarding.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StepProducts({ products, setProducts }) {
  return (
    <div data-testid="step-products">
      <StepHeader pre="Step 9 of 11" title="Add your first products." subtitle="You can always add more later \u2014 or skip this if you're selling services only." />
      <div className="space-y-3 max-w-2xl">
        {products.map((p, i) => (
          <div key={i} className="flex gap-2 items-center bg-[#15152E] border border-white/10 rounded-md p-2.5">
            <Input value={p.name} onChange={(e) => { const n = [...products]; n[i].name = e.target.value; setProducts(n); }} placeholder="Product name" className="border-0 bg-transparent text-white flex-1 h-10" />
            <Input type="number" value={p.price} onChange={(e) => { const n = [...products]; n[i].price = e.target.value; setProducts(n); }} placeholder="$" className="border-0 bg-transparent text-white w-24 h-10" />
            <Input value={p.category} onChange={(e) => { const n = [...products]; n[i].category = e.target.value; setProducts(n); }} placeholder="Category" className="border-0 bg-transparent text-white w-32 h-10" />
            {products.length > 1 && <button onClick={() => setProducts(products.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-500"><X size={16} /></button>}
          </div>
        ))}
      </div>
      <button onClick={() => setProducts([...products, { name: '', price: '', category: '' }])} className="text-xs text-white mt-4 flex items-center gap-1.5 hover:underline">
        <Plus size={12} /> Add another product
      </button>
    </div>
  );
}

function StepPayments({ setup, setSetup }) {
  const items = [
    { key: 'stripe',    name: 'Stripe',              desc: 'Accept credit & debit card payments.', icon: CreditCard },
    { key: 'etransfer', name: 'E-Transfer (Canada)', desc: 'Interac e-transfer payments.',          icon: HandCoins },
  ];
  return (
    <div data-testid="step-payments">
      <StepHeader pre="Step 9 of 11" title="Payment setup." subtitle="Pick what you want to accept. You can configure API keys later in Settings." />
      <div className="space-y-3 max-w-xl">
        {items.map(({ key, name, desc, icon: Icon }) => {
          const on = setup[key];
          return (
            <button
              key={key}
              onClick={() => setSetup(s => ({ ...s, [key]: !s[key] }))}
              className={`w-full flex items-center gap-4 px-5 py-5 rounded-md border text-left transition-all ${on ? 'bg-[#A78BFA] text-[#0E0E23] border-[#A78BFA]' : 'bg-[#15152E] text-white border-white/15 hover:border-white/40'}`}
              data-testid={`payment-${key}`}
            >
              <Icon size={20} />
              <div className="flex-1">
                <p className="text-[15px] font-bold">{name}</p>
                <p className={`text-xs mt-0.5 ${on ? 'text-white/70' : 'text-white/60'}`}>{desc}</p>
              </div>
              {on && <Check size={18} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepLaunch({ onLaunch, loading }) {
  return (
    <div data-testid="step-launch" className="pt-6">
      <StepHeader pre="Step 11 of 11" title={<>You&apos;re all set.<br />Let&apos;s go live.</>} subtitle="You can edit content, add products, and customise everything from your dashboard. You have 14 days free to explore." />
      <button
        onClick={onLaunch}
        disabled={loading}
        className="px-12 py-4 bg-[#1A1A1A] text-white text-xs tracking-[0.18em] font-bold uppercase hover:bg-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        data-testid="launch-site-btn"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <><Rocket size={14} /> Launch My Site</>}
      </button>
      <p className="mt-6 text-xs text-white/55 flex items-center gap-1.5"><Sparkles size={12} /> A confetti welcome awaits.</p>
    </div>
  );
}


function StepFonts({ value, setValue }) {
  // Group by tone, with the recommended pinned to top
  const groups = useMemo(() => {
    const order = ['Professional', 'Playful', 'Sophisticated', 'Bold', 'Quirky'];
    const map = {};
    FONT_OPTIONS.forEach(f => { (map[f.tone] = map[f.tone] || []).push(f); });
    return order.filter(t => map[t]).map(t => ({ tone: t, fonts: map[t] }));
  }, []);
  return (
    <div data-testid="step-fonts">
      <StepHeader pre="Step 10 of 11" title="Choose a font." subtitle="These 15 fonts were curated by our designers. Pick one — you can change it any time inside the editor." />
      <div className="space-y-8 max-w-2xl">
        {groups.map(({ tone, fonts }) => (
          <div key={tone}>
            <div className="flex items-center gap-3 mb-3">
              <p className="text-[15px] font-bold text-white">{tone}</p>
              {fonts.some(f => f.recommended) && (
                <span className="px-2.5 py-1 rounded-full bg-[#3B82F6]/15 text-[#1D4ED8] text-[10px] font-bold uppercase tracking-wider">Recommended</span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {fonts.map(f => {
                const on = value === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setValue(f.id)}
                    className={`relative bg-[#15152E] border rounded-md px-5 py-6 text-left transition-all ${on ? 'border-[#1A1A1A] shadow-[0_4px_20px_rgba(0,0,0,0.08)]' : 'border-white/10 hover:border-white/40'}`}
                    data-testid={`font-${f.id}`}
                  >
                    <p className="text-3xl text-white" style={{ fontFamily: f.family, lineHeight: 1 }}>Heading</p>
                    <p className="text-xs text-white/55 mt-3" style={{ fontFamily: f.family === f.body ? f.family : (f.body + ', sans-serif') }}>This is your paragraph.</p>
                    {on && <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center"><Check size={11} strokeWidth={3} /></span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
